import { $, $$, RM, root } from "./util.js";

// ─── Auto-motion prep (runs before initReveal) ─────────────────────────────
// Opts common blocks into the reveal system and staggers siblings, so templates
// don't have to hand-place `.rv` / `--d` on every element.
const AUTO_REVEAL = ".sec-head, .faq-intro, .faq-item, .tbl-wrap, .logos .lg, .dash, .cmp-stat";
const STAGGER_PARENTS = ".bento, .flip-grid, .snake-grid, .tl, .logos, .faq, .phones, .metric-row, .pkg ul, .hero-bento";

export function prepareMotion() {
  $$(AUTO_REVEAL).forEach((el) => el.classList.add("rv"));
  $$(".tl-wrap").forEach((el) => el.classList.add("draw"));

  $$(STAGGER_PARENTS).forEach((parent) => {
    let i = 0;
    Array.prototype.forEach.call(parent.children, (el) => {
      if (!el.classList.contains("rv")) return;
      if (el.style.getPropertyValue("--d") === "") el.style.setProperty("--d", Math.min(i, 6));
      i++;
    });
  });

  // Sparkline: normalise the stroke length so CSS can "draw" it.
  $$(".spark path.l").forEach((p) => p.setAttribute("pathLength", "1"));
}

// ─── Count-up for stat figures ─────────────────────────────────────────────
// "−38%", "2,100", "$18.4K", "4.1x", "48 hrs", "18,000+" — prefix/suffix kept,
// decimals and thousands separators preserved. Final text always equals the
// original markup, so no-JS / reduced-motion / SEO see the real figure.
const NUM = /^([^\d]*)(\d[\d,]*(?:\.\d+)?)(.*)$/;

function countUp(el) {
  const original = el.textContent;
  const m = original.trim().match(NUM);
  if (!m) return;
  const [, pre, numStr, suf] = m;
  const target = parseFloat(numStr.replace(/,/g, ""));
  if (!isFinite(target) || target === 0) return;
  const decimals = (numStr.split(".")[1] || "").length;
  const commas = numStr.indexOf(",") > -1;
  const render = (v) =>
    pre + v.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals, useGrouping: commas }) + suf;
  const dur = 1300;
  const start = performance.now();
  const tick = (now) => {
    const k = Math.min(1, (now - start) / dur);
    const eased = 1 - Math.pow(1 - k, 3);
    el.textContent = k < 1 ? render(target * eased) : original;
    if (k < 1) requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

export function initCountUp() {
  if (RM || !("IntersectionObserver" in window)) return;
  const io = new IntersectionObserver(
    (es) =>
      es.forEach((e) => {
        if (e.isIntersecting) {
          io.unobserve(e.target);
          countUp(e.target);
        }
      }),
    { threshold: 0.6 }
  );
  $$(".big-num .ph, .metric b .ph, .cmp-stat b .ph, .dash-body .t b .ph, .pkg .price .ph").forEach((el) => io.observe(el));
}

// ─── Reading-progress bar under the header ─────────────────────────────────
export function initProgress() {
  const bar = document.createElement("div");
  bar.className = "scroll-progress";
  bar.setAttribute("aria-hidden", "true");
  document.body.appendChild(bar);
  let ticking = false;
  const update = () => {
    ticking = false;
    const max = root.scrollHeight - innerHeight;
    bar.style.transform = "scaleX(" + (max > 0 ? Math.min(1, scrollY / max) : 0) + ")";
  };
  window.addEventListener(
    "scroll",
    () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    },
    { passive: true }
  );
  window.addEventListener("resize", update);
  update();
}
